import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ArrowRight,
  CircleDot,
  Car,
  Bike,
  Truck,
  Factory,
  Wallet,
  ShieldCheck,
  BriefcaseBusiness,
  Building2,
  Zap,
} from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';

// ---------- Client mix (anonymised buckets) ----------
const CLIENT_MIX = [
  { label: 'Global OEMs', count: 12, hint: 'PV · CV · 2W · 3W · EV', tone: 'amber' },
  { label: 'Indian OEMs', count: 8, hint: 'Listed & marquee unlisted', tone: 'amber' },
  { label: 'Tier-1 component manufacturers', count: 15, hint: 'Powertrain · braking · electronics', tone: 'cyan' },
  { label: 'Tyre majors', count: 4, hint: 'Global + Indian top-5', tone: 'cyan' },
  { label: 'Vehicle insurers', count: 6, hint: 'Public &amp; private', tone: 'cyan' },
  { label: 'Auto-financiers &amp; NBFCs', count: 9, hint: 'PV · CV · Used vehicle', tone: 'amber' },
  { label: 'Strategy consulting firms', count: 5, hint: 'Big-4, MBB, boutiques', tone: 'white' },
  { label: 'Large dealer groups', count: 20, hint: 'Multi-brand &amp; multi-city', tone: 'white' },
  { label: 'Aftermarket &amp; workshop chains', count: 7, hint: 'Multi-brand networks', tone: 'cyan' },
  { label: 'Mobility / EV ventures', count: 11, hint: 'Funded &amp; growth-stage', tone: 'amber' },
];

const TONE_CLS = {
  amber: 'text-amber border-amber/30 bg-amber/5',
  cyan: 'text-cyan border-cyan/30 bg-cyan/5',
  white: 'text-white border-white/20 bg-white/5',
};

// ---------- Use cases ----------
const USE_CASES = [
  {
    icon: CircleDot,
    sector: 'Tyre major · global top-5',
    title: 'Network expansion across Tier-2/3 India',
    brief: 'Identify high-potential town clusters for retail network expansion in PV &amp; 2W replacement tyres.',
    approach: 'Vahan-grade vehicle parc density + segment-level age curves + competitor outlet mapping at PIN-code resolution.',
    metrics: [
      { v: '18', l: 'New town clusters identified' },
      { v: '40%', l: 'Faster expansion decisioning' },
      { v: '₹300Cr+', l: 'Retail revenue unlocked' },
    ],
    span: 'md:col-span-2',
  },
  {
    icon: Car,
    sector: '4-wheeler PV OEM · top-3 India',
    title: 'Product strategy &amp; segment penetration · compact SUV',
    brief: 'Find white-space in the compact SUV segment and design variant mix for the next launch.',
    approach: '~400M registration database + competitor variant performance + customer journey decoding across 1,500+ RTOs.',
    metrics: [
      { v: '+12%', l: 'Segment share · 8 quarters' },
      { v: '4', l: 'Variant-mix scenarios modelled' },
      { v: 'Top-3', l: 'Compact SUV rank achieved' },
    ],
    span: '',
  },
  {
    icon: Bike,
    sector: '2-wheeler OEM · premium global',
    title: 'Dealer network rationalisation across 250 outlets',
    brief: 'Uneven dealership performance, leaking funnel — restructure the network without losing volume.',
    approach: 'SLA-enforced lead management (DLIP) + showroom footfall capture + AI lead scoring + delivered-deal locks.',
    metrics: [
      { v: '+22%', l: 'Lead-to-delivery conversion' },
      { v: '8', l: 'Underperformers consolidated' },
      { v: '₹180Cr', l: 'Annualised revenue protected' },
    ],
    span: '',
  },
  {
    icon: Factory,
    sector: 'Tier-1 component manufacturer',
    title: 'Aftermarket diversification · braking systems',
    brief: 'Reduce OEM-revenue concentration by building a structured aftermarket play.',
    approach: 'Workshop network mapping + SKU-level demand by region + EV-readiness scoring across product portfolio.',
    metrics: [
      { v: '14% → 28%', l: 'Aftermarket revenue mix' },
      { v: '24 mo', l: 'Transformation horizon' },
      { v: '6', l: 'New distribution hubs' },
    ],
    span: 'md:col-span-2',
  },
  {
    icon: Wallet,
    sector: 'Commercial-vehicle financier · large NBFC',
    title: 'Underwriting on real lifecycle data · used CV',
    brief: 'Cut non-performing assets in used commercial vehicle financing without slowing growth.',
    approach: 'Chassis-linked ownership &amp; service history + dealer-of-origin risk scoring + asset-tracking telemetry.',
    metrics: [
      { v: '+35%', l: 'Approval throughput' },
      { v: '~120 bps', l: 'NPA reduction' },
      { v: '0', l: 'Loss in growth rate' },
    ],
    span: 'md:col-span-2',
  },
  {
    icon: ShieldCheck,
    sector: 'Motor insurer · global top-5',
    title: 'Claims fraud intelligence',
    brief: 'Detect suspicious claims clusters before payout, without slowing genuine settlements.',
    approach: 'Workshop network footprint + repeat-incident graph analysis + telematics signal correlation.',
    metrics: [
      { v: '+27%', l: 'Fraud-flag detection' },
      { v: '-18%', l: 'Genuine claims TAT' },
      { v: '4', l: 'States piloted in 6 months' },
    ],
    span: '',
  },
  {
    icon: BriefcaseBusiness,
    sector: 'Global strategy consulting firm',
    title: 'India automotive market sizing for a sovereign client',
    brief: 'Quantify the addressable opportunity in Indian automotive for a sovereign investor.',
    approach: 'Vahan + SIAM + FADA + custom EV trajectory modelling, delivered as white-labelled data &amp; analysis.',
    metrics: [
      { v: '$1B+', l: 'Market-entry decision informed' },
      { v: '12', l: 'State-level deep dives' },
      { v: 'White-label', l: 'End-product format' },
    ],
    span: '',
  },
  {
    icon: Building2,
    sector: 'Premium global PV OEM · luxury',
    title: 'India entry &amp; city-launch sequencing',
    brief: 'Decide the right city portfolio &amp; dealer footprint for an India launch.',
    approach: 'HNI density mapping + luxury PV penetration by RTO + premium segment trend lines + showroom catchment design.',
    metrics: [
      { v: '7', l: 'Cities · phased launch' },
      { v: '18 mo', l: 'To dealer ROI targets' },
      { v: 'Top-3', l: 'Luxury PV rank by yr-2' },
    ],
    span: 'md:col-span-2',
  },
  {
    icon: Truck,
    sector: 'Commercial-vehicle OEM · domestic leader',
    title: 'Penetration in growing CV sub-segments',
    brief: 'Identify and capture share in fast-growing CV body-types and applications.',
    approach: 'Application-level Vahan data + fleet-operator interviews + dealer demand-signal capture across 600+ touchpoints.',
    metrics: [
      { v: '3', l: 'New sub-segments entered' },
      { v: '+9%', l: 'Share in tipper segment' },
      { v: '24', l: 'New dealer appointments' },
    ],
    span: '',
  },
  {
    icon: Zap,
    sector: 'EV-first 2W brand · growth stage',
    title: 'Customer journey intelligence across 750 dealerships',
    brief: 'Capture every customer\'s journey end-to-end without depending on dealer personnel to log it.',
    approach: 'QR + WhatsApp customer-side capture + dealer-blind shadow funnel + Indic voice-AI on service calls.',
    metrics: [
      { v: '750+', l: 'Dealerships in scope' },
      { v: '<30s', l: 'Customer survey time' },
      { v: 'T+0', l: 'Time to insight' },
    ],
    span: '',
  },
];

// ---------- Decorative constellation ----------
// 36 dots positioned pseudo-randomly to form an abstract client constellation
const CONSTELLATION = Array.from({ length: 36 }).map((_, i) => {
  const seed = (i * 9301 + 49297) % 233280;
  const x = (seed % 100);
  const y = ((seed * 7) % 100);
  const size = [3, 4, 5, 6, 8][i % 5];
  const tone = ['#F59E0B', '#06B6D4', '#F59E0B', '#FFFFFF', '#06B6D4'][i % 5];
  const delay = (i % 7) * 0.25;
  return { x, y, size, tone, delay };
});

const Constellation = () => (
  <div className="relative w-full h-72 md:h-80 bg-ink-900 border border-white/10 overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-50" />
    <div className="absolute inset-0 radial-glow-amber opacity-30" />
    <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
      {/* connecting lines */}
      {CONSTELLATION.slice(0, 24).map((d, i) => {
        const next = CONSTELLATION[(i + 1) % CONSTELLATION.length];
        return (
          <line
            key={`l-${i}`}
            x1={d.x}
            y1={d.y * 0.6}
            x2={next.x}
            y2={next.y * 0.6}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="0.15"
          />
        );
      })}
      {/* dots */}
      {CONSTELLATION.map((d, i) => (
        <g key={i}>
          <circle cx={d.x} cy={d.y * 0.6} r={d.size / 4} fill={d.tone} opacity="0.85">
            <animate
              attributeName="opacity"
              values="0.3;1;0.3"
              dur={`${2.4 + (i % 4) * 0.6}s`}
              begin={`${d.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
          <circle cx={d.x} cy={d.y * 0.6} r={d.size / 3} fill={d.tone} opacity="0.15">
            <animate
              attributeName="r"
              values={`${d.size / 3};${d.size / 1.5};${d.size / 3}`}
              dur={`${2.4 + (i % 4) * 0.6}s`}
              begin={`${d.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        </g>
      ))}
    </svg>
    {/* corner legends */}
    <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
      Client constellation
    </div>
    <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
      Anonymised · NDA-respected
    </div>
    <div className="absolute bottom-4 left-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-amber" /> OEMs &amp; mobility ventures
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-cyan" /> Component · insurance · finance
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-white" /> Consulting · dealer groups
      </span>
    </div>
  </div>
);

// ---------- India impact map (states × engagements) ----------
const STATE_IMPACT = [
  { code: 'MH', name: 'Maharashtra',     count: 18, x: 32, y: 58 },
  { code: 'DL', name: 'Delhi NCR',       count: 17, x: 44, y: 26 },
  { code: 'KA', name: 'Karnataka',       count: 16, x: 42, y: 78 },
  { code: 'TN', name: 'Tamil Nadu',      count: 15, x: 52, y: 84 },
  { code: 'GJ', name: 'Gujarat',         count: 12, x: 24, y: 44 },
  { code: 'UP', name: 'Uttar Pradesh',   count: 11, x: 52, y: 30 },
  { code: 'HR', name: 'Haryana',         count: 10, x: 42, y: 28 },
  { code: 'TS', name: 'Telangana',       count: 9,  x: 48, y: 64 },
  { code: 'KL', name: 'Kerala',          count: 8,  x: 42, y: 92 },
  { code: 'WB', name: 'West Bengal',     count: 7,  x: 76, y: 42 },
  { code: 'RJ', name: 'Rajasthan',       count: 7,  x: 32, y: 32 },
  { code: 'PB', name: 'Punjab',          count: 6,  x: 38, y: 22 },
  { code: 'MP', name: 'Madhya Pradesh',  count: 6,  x: 44, y: 44 },
  { code: 'AP', name: 'Andhra Pradesh',  count: 5,  x: 54, y: 72 },
  { code: 'OD', name: 'Odisha',          count: 4,  x: 64, y: 54 },
  { code: 'NE', name: 'North-East',      count: 4,  x: 88, y: 36 },
  { code: 'JH', name: 'Jharkhand',       count: 3,  x: 68, y: 42 },
  { code: 'AS', name: 'Assam',           count: 3,  x: 84, y: 40 },
  { code: 'BR', name: 'Bihar',           count: 3,  x: 66, y: 34 },
  { code: 'UK', name: 'Uttarakhand',     count: 3,  x: 48, y: 22 },
  { code: 'HP', name: 'Himachal',        count: 2,  x: 40, y: 18 },
  { code: 'CG', name: 'Chhattisgarh',    count: 2,  x: 56, y: 50 },
  { code: 'CH', name: 'Chandigarh',      count: 2,  x: 41, y: 24 },
  { code: 'GA', name: 'Goa',             count: 2,  x: 32, y: 72 },
  { code: 'JK', name: 'J&K',             count: 1,  x: 36, y: 12 },
  { code: 'PY', name: 'Puducherry',      count: 1,  x: 56, y: 88 },
];

const stateStyle = (n) => {
  if (n >= 15) return { r: 2.4, fill: '#F59E0B', glow: 'rgba(245,158,11,0.45)', text: '#F59E0B' };
  if (n >= 10) return { r: 1.9, fill: '#F59E0B', glow: 'rgba(245,158,11,0.3)', text: 'rgba(255,255,255,0.85)' };
  if (n >= 5)  return { r: 1.5, fill: 'rgba(245,158,11,0.75)', glow: 'rgba(245,158,11,0.18)', text: 'rgba(255,255,255,0.6)' };
  if (n >= 2)  return { r: 1.2, fill: 'rgba(245,158,11,0.45)', glow: 'rgba(245,158,11,0.1)',  text: 'rgba(255,255,255,0.45)' };
  return       { r: 1.0, fill: 'rgba(255,255,255,0.4)',        glow: 'rgba(255,255,255,0.05)', text: 'rgba(255,255,255,0.35)' };
};

const IndiaImpactMap = () => {
  const [hover, setHover] = useState(null);
  const sorted = [...STATE_IMPACT].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, 6);
  const sumEng = STATE_IMPACT.reduce((s, x) => s + x.count, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* MAP */}
      <div className="lg:col-span-7 relative">
        <div className="relative w-full aspect-[5/6] bg-ink-900 border border-white/10 overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-50" />
          <div className="absolute inset-0 radial-glow-amber opacity-30" />
          <svg viewBox="0 0 100 120" className="absolute inset-0 w-full h-full">
            <path
              d="M30 14 L42 10 L55 14 L62 12 L72 18 L82 22 L92 32 L88 42 L82 50 L78 62 L68 78 L58 90 L52 100 L46 106 L40 102 L36 92 L30 80 L22 66 L18 54 L20 42 L24 30 L28 22 Z"
              fill="rgba(245,158,11,0.04)"
              stroke="rgba(245,158,11,0.25)"
              strokeWidth="0.3"
            />
            <g stroke="rgba(255,255,255,0.05)" strokeWidth="0.15" strokeDasharray="0.6 0.6">
              {[20, 40, 60, 80, 100].map((y) => (
                <line key={y} x1="0" y1={y} x2="100" y2={y} />
              ))}
              {[20, 40, 60, 80].map((x) => (
                <line key={x} x1={x} y1="0" x2={x} y2="120" />
              ))}
            </g>
            {STATE_IMPACT.map((s, i) => {
              const sz = stateStyle(s.count);
              const isHover = hover === i;
              return (
                <g
                  key={s.code}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx={s.x} cy={s.y} r={sz.r * 2.6} fill={sz.glow} />
                  <circle cx={s.x} cy={s.y} r={sz.r} fill={sz.fill} />
                  {isHover && (
                    <circle
                      cx={s.x}
                      cy={s.y}
                      r={sz.r * 1.9}
                      fill="none"
                      stroke="#F59E0B"
                      strokeWidth="0.3"
                    />
                  )}
                  <text
                    x={s.x + sz.r + 0.9}
                    y={s.y + 0.6}
                    fill={isHover ? '#F59E0B' : sz.text}
                    fontSize="1.9"
                    fontFamily="'IBM Plex Mono', monospace"
                    style={{ letterSpacing: '0.05em' }}
                  >
                    {s.code}
                  </text>
                  {/* invisible bigger hover target */}
                  <circle cx={s.x} cy={s.y} r="3.2" fill="transparent" />
                </g>
              );
            })}
          </svg>

          {hover !== null && (
            <div
              className="absolute pointer-events-none z-10"
              style={{
                left: `${STATE_IMPACT[hover].x}%`,
                top: `${(STATE_IMPACT[hover].y / 120) * 100}%`,
                transform: 'translate(-50%, calc(-100% - 14px))',
              }}
            >
              <div className="px-3 py-2 bg-black/95 border border-amber/50 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.16em] whitespace-nowrap shadow-xl">
                <div className="text-white">{STATE_IMPACT[hover].name}</div>
                <div className="text-amber mt-1 tabular-nums">
                  {STATE_IMPACT[hover].count} engagements
                </div>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-amber/50" />
            </div>
          )}

          <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Engagement footprint · {STATE_IMPACT.length} states &amp; UTs
          </div>
          <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
            Hover any node
          </div>
          <div className="absolute bottom-3 left-4 right-4 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
            <span>Intensity</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber" /> 15+</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber/75" /> 5–14</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber/45" /> 2–4</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-white/40" /> 1</span>
          </div>
        </div>
      </div>

      {/* SIDE PANEL */}
      <div className="lg:col-span-5 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/10">
          <div className="bg-ink-900 p-5">
            <div className="font-display text-3xl text-cyan tabular-nums">
              {STATE_IMPACT.length}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
              States &amp; UTs touched
            </div>
          </div>
          <div className="bg-ink-900 p-5">
            <div className="font-display text-3xl text-cyan tabular-nums">{sumEng}+</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
              State-engagements delivered
            </div>
          </div>
        </div>

        <div className="bg-ink-800 border border-white/10 p-6 flex-1">
          <div className="overline text-amber mb-5">Top 6 by engagement count</div>
          <ul className="divide-y divide-white/5">
            {top.map((s, i) => (
              <li
                key={s.code}
                onMouseEnter={() => setHover(STATE_IMPACT.indexOf(s))}
                onMouseLeave={() => setHover(null)}
                className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:text-amber"
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-[10px] text-zinc-500 w-5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cyan border border-cyan/30 px-1.5 py-0.5">
                    {s.code}
                  </span>
                  <span className="text-zinc-200 text-sm truncate">{s.name}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="hidden md:block w-20 h-1 bg-white/10">
                    <span
                      className="block h-full bg-amber"
                      style={{ width: `${(s.count / sorted[0].count) * 100}%` }}
                    />
                  </span>
                  <span className="font-mono text-sm text-amber tabular-nums w-6 text-right">
                    {s.count}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-5 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 leading-relaxed">
            Numbers indicative · synthesised across active &amp; past engagements.
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Page ----------
const UseCases = () => {
  return (
    <PageShell>
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 radial-glow-amber opacity-40" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Use cases · Clients · Industry impact
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-5xl leading-[1.02]">
            We don't put their names on a wall. <br className="hidden md:block" />
            We put their <span className="text-amber">numbers on the board.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            Almost every engagement we run sits under NDA. So this page never
            names a client. What it does name — clearly — is the brief, the
            approach, and the result. Across tyre majors, OEMs, Tier-1
            component manufacturers, insurers, financiers and global consulting
            firms.
          </p>
        </div>
      </section>

      {/* CLIENT MIX + CONSTELLATION */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 bg-black border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-10">
          <div className="lg:col-span-5 flex flex-col justify-between">
            <SectionHeader
              kicker="Client mix"
              title="The shape of who we work with."
              intro="A live, anonymised breakdown of our client base — OEMs to insurers, tyre majors to NBFCs, strategy firms to dealer groups."
            />
            <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/10 mt-4">
              {[
                ['~80+', 'Active &amp; past engagements'],
                ['11', 'Client archetypes'],
                ['28', 'Indian states &amp; UTs touched'],
              ].map(([v, l]) => (
                <div key={l} className="bg-ink-900 p-5">
                  <div className="font-display text-2xl md:text-3xl text-cyan tabular-nums">{v}</div>
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug"
                    dangerouslySetInnerHTML={{ __html: l }}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <Constellation />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {CLIENT_MIX.map((c) => (
            <div
              key={c.label}
              className={`border p-5 transition-all hover:scale-[1.03] ${TONE_CLS[c.tone]}`}
            >
              <div className="font-display text-3xl tabular-nums">{c.count}</div>
              <div
                className="font-mono text-[10px] uppercase tracking-[0.18em] mt-3 leading-snug opacity-90"
                dangerouslySetInnerHTML={{ __html: c.label }}
              />
              <div
                className="font-mono text-[9px] uppercase tracking-[0.16em] opacity-60 mt-2"
                dangerouslySetInnerHTML={{ __html: c.hint }}
              />
            </div>
          ))}
        </div>

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 max-w-3xl leading-relaxed">
          Numbers are indicative of active &amp; past engagements · names &amp;
          logos withheld out of contractual respect.
        </p>
      </section>

      {/* USE CASES BENTO */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <SectionHeader
          kicker="Use cases"
          title="Briefs we have run. Outcomes we have shipped."
          intro="Industry-by-industry — what came in the door, what we did about it, and what moved on the board."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
          {USE_CASES.map((u) => {
            const Icon = u.icon;
            return (
              <article
                key={u.title}
                className={`group relative bg-ink-800 border border-white/10 p-7 hover:border-amber/50 transition-colors flex flex-col hover-trace ${u.span}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="grid h-9 w-9 place-items-center bg-amber/10 border border-amber/40">
                    <Icon className="h-4 w-4 text-amber" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1 text-right">
                    {u.sector}
                  </span>
                </div>

                <h3
                  className="font-display text-xl md:text-2xl text-white leading-snug mb-4"
                  dangerouslySetInnerHTML={{ __html: u.title }}
                />

                <div className="space-y-3 text-sm leading-relaxed mb-6">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Brief</div>
                    <p className="text-zinc-300" dangerouslySetInnerHTML={{ __html: u.brief }} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Approach</div>
                    <p className="text-zinc-400" dangerouslySetInnerHTML={{ __html: u.approach }} />
                  </div>
                </div>

                <div className="mt-auto pt-5 border-t border-white/10 grid grid-cols-3 gap-3">
                  {u.metrics.map((m) => (
                    <div key={m.l}>
                      <div className="font-display text-base md:text-lg text-amber tabular-nums leading-tight">
                        {m.v}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500 mt-1 leading-snug">
                        {m.l}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 max-w-3xl leading-relaxed">
          Outcomes are indicative · drawn from anonymised engagements · sharable references can be arranged under NDA on request.
        </p>
      </section>

      {/* INDIA IMPACT MAP */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24 bg-black border-y border-white/10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeader
            kicker="Where we have delivered"
            title="An India map of engagements."
            intro="A live, interactive view of the states and union territories where our work has landed — colour-graded by engagement intensity. Hover any node for the count."
          />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">
            Anonymised · indicative counts
          </div>
        </div>
        <IndiaImpactMap />
      </section>

      {/* THEMES BAND */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 bg-black border-y border-white/10">
        <SectionHeader
          kicker="The themes we keep being called for"
          title="Four briefs in four words."
          intro="Different industries. Different problems. Same handful of asks."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
          {[
            { t: 'Network development', d: 'Where to open, where to close, where to invest — at PIN-code resolution.' },
            { t: 'Product strategy', d: 'Variant mix, segment positioning, price ladders — informed by real registration data.' },
            { t: 'Penetration · growth markets', d: 'Tier-2/3, EV, used vehicle, application-specific CV — where the next decade plays out.' },
            { t: 'Customer journey intelligence', d: 'From walk-in to repeat service — capturing the truth without depending on dealers.' },
          ].map((b) => (
            <div key={b.t} className="bg-ink-900 p-7">
              <h3 className="font-display text-xl text-white mb-3">{b.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 md:px-12 lg:px-16 py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow-amber opacity-50" />
        <div className="relative max-w-4xl">
          <div className="overline text-amber mb-4">Bring us your brief</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.02]">
            If it sits in automotive, mobility or the data around it — there's a strong chance we've seen a version of your brief before.
          </h2>
          <p className="mt-6 text-zinc-300 text-lg max-w-2xl">
            We are happy to walk through closer references under NDA. Tell us
            what you're trying to move, and we'll come ready with the numbers.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Bring us a brief
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/solutions"
              className="inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-white hover:bg-white/5 transition-colors"
            >
              See how we partner <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default UseCases;
